import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class RouletteService {
    readonly doubleZero = 37;

    private readonly minNumber = 1;
    private readonly maxNumber = 36;
    private readonly zero = 0;

    private readonly lowHalfStart = 1;
    private readonly lowHalfEnd = 18;
    private readonly highHalfStart = 19;
    private readonly highHalfEnd = 36;

    private readonly firstDozenStart = 1;
    private readonly firstDozenEnd = 12;
    private readonly secondDozenStart = 13;
    private readonly secondDozenEnd = 24;
    private readonly thirdDozenStart = 25;
    private readonly thirdDozenEnd = 36;

    private readonly firstRedOddStart = 1;
    private readonly firstRedOddEnd = 10;
    private readonly secondRedOddStart = 19;
    private readonly secondRedOddEnd = 28;

    private readonly divisorForEven = 2;

    /**
     * Determines the primary color of the number based on the wheel using mathematical pattern logic.
     * This is highly efficient (O(1) complexity) and eliminates the need for large lookup arrays.
     * @param number The spun number (0-37).
     */
    getRouletteColor(number: number): 'black' | 'green' | 'red' | null {
        if (number === this.zero || number === this.doubleZero) {
            return 'green';
        }

        // Handle non-standard numbers outside the 1-37 range for robustness
        if (number < this.minNumber || number > this.maxNumber) {
            return 'black';
        }

        const isOdd = number % this.divisorForEven !== this.zero;

        if (
            (number >= this.firstRedOddStart && number <= this.firstRedOddEnd) ||
            (number >= this.secondRedOddStart && number <= this.secondRedOddEnd)
        ) {
            return isOdd ? 'red' : 'black';
        }

        return isOdd ? 'black' : 'red';
    }

    /**
     * Maps the color to the custom CSS class names defined in your SCSS.
     * This ensures the custom gradients (like background-red) are applied correctly.
     * @param number The spun number.
     */
    getBackgroundColorClass(number: number): string {
        const color = this.getRouletteColor(number);

        switch (color) {
            case 'red':
                return 'background-red';
            case 'black':
                return 'background-black';
            case 'green':
                return 'background-green';
            default:
                return 'background-red';
        }
    }

    /**
     * Determines if the number is Even, Odd, or N/A (for 0).
     * @param number The spun number (0-36).
     */
    isEvenOrOdd(number: number): 'Even' | 'Odd' | null {
        if (number < this.minNumber || number > this.maxNumber) {
            return null;
        }

        return number % this.divisorForEven === this.zero ? 'Even' : 'Odd';
    }

    /**
     * Determines if the number is in the Low (1-18) or High (19-36) half, or N/A.
     * @param number The spun number (0-36).
     */
    getHalf(number: number): '1-18' | '19-36' | null {
        if (number >= this.lowHalfStart && number <= this.lowHalfEnd) {
            return '1-18';
        }

        if (number >= this.highHalfStart && number <= this.highHalfEnd) {
            return '19-36';
        }

        return null;
    }

    /**
     * Determines which Dozen (Third) the number falls into, or N/A.
     * @param number The spun number (0-36).
     */
    getThird(number: number): '1-12' | '13-24' | '25-36' | null {
        if (number >= this.firstDozenStart && number <= this.firstDozenEnd) {
            return '1-12';
        }

        if (number >= this.secondDozenStart && number <= this.secondDozenEnd) {
            return '13-24';
        }

        if (number >= this.thirdDozenStart && number <= this.thirdDozenEnd) {
            return '25-36';
        }

        return null;
    }

    /**
     * Returns the display value for a number (37 becomes '00').
     * @param number The internal number (0-37).
     */
    getDisplayValue(number: number): number | string {
        return number === this.doubleZero ? '00' : number;
    }
}
